<template>
  <Layout>
    <div class="view-statement">
      <div class="router-view">
        <Transition name="fade" mode="out-in">
          <div v-if="statementToShow" :key="statementToShow.id">
            <LayoutStatement :data-cy="`statement-${statementToShow.id}`">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div v-html="statementToShow.content" />

              <AppStatementCta
                :loading="loading"
                :comment="statementToShow.comment"
                :require-more-info="statementToShow.requireMoreInfo"
                :require-more-info-message="statementToShow.requireMoreInfoMessage"
                @submitted="handleConfirm"
              />
            </LayoutStatement>
          </div>
        </Transition>
      </div>
    </div>
  </Layout>
</template>

<script>
import { mapGetters } from 'vuex'
import Layout from '@/router/layouts/Main'
import axiosQuoteApi from '@/services/axiosQuoteApi'
import axiosRatingEngine from '@/services/axiosRatingEngine'
import LayoutStatement from '@/router/layouts/Statement.vue'
import AppStatementCta from '@/components/statement/Cta.vue'
import rateMixin from '@/mixins/rate'

export default {
  components: {
    Layout,
    LayoutStatement,
    AppStatementCta,
  },
  mixins: [rateMixin],
  data () {
    return {
      loading: false,
    }
  },
  computed: {
    ...mapGetters(['isCouponWithFreeLegalEntered', 'isOnlyLegalExpensesSelected']),
    id () {
      // get statement id from route
      return this.$route.params.id
    },
    statements () {
      return this.$store.getters.getStatements
    },
    statementToShow () {
      // get the statement to display based on the id
      return this.statements.filter(x => x.id === this.id)[0]
    },
    referQuote: {
      get () {
        return this.$store.getters.getReferQuote
      },
      set (value) {
        this.$store.commit('updateReferQuote', value)
      },
    },
    removedProducts: {
      get () {
        return this.$store.getters.getRemovedProducts
      },
      set (value) {
        this.$store.commit('updateRemovedProducts', value)
      },
    },
    configuredProducts () {
      return this.$store.getters.getProducts
    },
    isValidProducts () {
      if (!this.isCouponWithFreeLegalEntered && this.isOnlyLegalExpensesSelected) return false

      const managementLiability = this.configuredProducts.filter(product => product.key === 'ManagementLiability')
      if (managementLiability.length) {
        const dAndO = managementLiability[0].rates.filter(
          rate => rate.key === 'ManagementLiability\\DirectorsAndOfficers',
        )
        if (!dAndO.length) return false
      }
      return true
    },
  },
  watch: {
    $route (to, from) {
      setTimeout(() => {
        const statement = document.getElementsByClassName('view-statement')[0]
        // Fix: if function for Cypress Headless E2E Testing (using electron 59) - https://github.com/cypress-io/cypress/issues/2761
        if (typeof statement.scrollTo === 'function') {
          statement.scrollTo(0, 0)
        }
      }, 500)
    },
  },
  methods: {
    handleConfirm (data) {
      // get the statement index from all the statements
      const statementIndex = this.statements.findIndex(x => x.id === this.statementToShow.id)
      // build statement to save to vuex
      const statement = {
        id: this.statementToShow.id,
      }
      statement.agreed = data.agreed
      if (data.comment) {
        statement.comment = data.comment
      }
      // Commit it to vuex
      this.$store.commit('updateStatement', statement)
      // if it's not the last statement, push route to next statement by it's index
      if (statementIndex < this.statements.length - 1) {
        this.$router.push(`/statements/${this.statements[statementIndex + 1].id}`)
      } else {
        // else submit statements
        this.submitStatements()
      }
    },
    submitStatements () {
      this.loading = true
      const statementsData = []

      this.$store.getters.getStatements.forEach((statement) => {
        const newStatement = {
          id: statement.id,
          agreed: statement.agreed,
          content: statement.content,
          requireMoreInfo: statement.requireMoreInfo,
          requireMoreInfoMessage: statement.requireMoreInfoMessage,
        }
        if (statement.comment) newStatement.comment = statement.comment
        statementsData.push(newStatement)
      })

      // post to rating engine and then api
      this.postToRatingEngine(statementsData).then(() => this.sendToApi(statementsData))
    },
    async postToRatingEngine (statementsData) {
      await axiosRatingEngine
        .post('api/refer', { statements: statementsData })
        .then((response) => {
          this.referQuote = response.data.refer
          if (response.data.remove && response.data.remove.length) {
            const productsToRemove = []
            response.data.remove.forEach((product) => {
              this.configuredProducts.forEach((configuredProduct) => {
                configuredProduct.rates.forEach((rate) => {
                  if (product === rate.key) productsToRemove.push(product)
                })
              })
            })
            this.removedProducts = productsToRemove
          }
        })
        .catch(() => {
          this.loading = false
          this.$router.push('/404')
        })
    },
    sendToApi (statementsData) {
      this.removeRates().then(() => {
        axiosQuoteApi
          .put(`quote/${this.$store.state.quoteId}/statement`, statementsData)
          .then((response) => {
            this.loading = false
            if (this.referQuote || !this.isValidProducts || this.configuredProducts.length === 0) {
              this.$router.push({ name: 'refer-quote' })
            } else {
              this.$router.push({ name: 'quote confirmation' })
            }
          })
          .catch(() => {
            this.loading = false
            this.$router.push('/404')
          })
      })
    },
    async removeRates () {
      if (this.removedProducts.length === 0) return

      await this.removeRate(this.removedProducts)

      await this.$store.dispatch('updateQuote', this.$store.getters.getEstimate)
    },
  },
}
</script>
